'use client';

import 'BomdisoftComponents/connect-agent-dialog/styles.css';
import { Button } from 'BomdisoftComponents/buttons/index';
import { AddressCopy } from 'BomdisoftComponents/connect-agent-dialog/address-copy';
import { AGENT_CLIENTS, type AgentClient, agentClient, CLIENT_GUIDES, type ConnectAgentProduct, stepAround, toConnectAgentSignIn } from 'BomdisoftComponents/connect-agent-dialog/render';
import { Dialog, DialogActions, DialogContent, DialogTitle } from 'BomdisoftComponents/dialog/index';
import { ToggleButton, ToggleGroup } from 'BomdisoftComponents/inputs/toggle-group/index';
import { usePersisted } from '@bomdisoft/store/persisted/react';
import { connectAgentAddressText, connectAgentClientText, connectAgentDoneText, connectAgentProductText, connectAgentText, type SupportedLanguage } from '@bomdisoft/translations';
import { type ReactNode, useId, useState } from 'react';

export type ConnectAgentDialogProps = {
  language: SupportedLanguage;
  onClose: () => void;
  open: boolean;
  products: ConnectAgentProduct[];
  testId: string;
};

export { AGENT_CLIENTS, type AgentClient, agentClient, CLIENT_GUIDES, type ConnectAgentProduct, stepAround } from 'BomdisoftComponents/connect-agent-dialog/render';

export const ConnectAgentDialog = ({ language, onClose, open, products, testId }: ConnectAgentDialogProps): ReactNode => {
  const [client, setClient] = usePersisted(agentClient);
  const [productKey, setProductKey] = useState<string | null>(null);
  const labelId = useId();
  const product = products.find(entry => entry.key === productKey) ?? products[0];
  const guide = CLIENT_GUIDES[client];
  const signIn = toConnectAgentSignIn(product);
  const { after, before } = stepAround(guide.step[language]);

  return (
    <Dialog onClose={onClose} open={open} testId={testId}>
      <DialogTitle>{connectAgentText[language]}</DialogTitle>
      <DialogContent>
        <div className='bomdisoft-connect-agent-dialog-connect'>
          {products.length > 1 ? (
            <ToggleGroup aria-label={connectAgentProductText[language]} className='bomdisoft-connect-agent-dialog-choice' data-testid={`${testId}-product`} onChange={next => next && setProductKey(next)} size='small' value={product?.key ?? null}>
              {products.map(entry => (
                <ToggleButton data-testid={`${testId}-product-${entry.key}`} key={entry.key} value={entry.key}>
                  {entry.name}
                </ToggleButton>
              ))}
            </ToggleGroup>
          ) : null}
          <div className='bomdisoft-connect-agent-dialog-field'>
            <span className='bomdisoft-connect-agent-dialog-field-label' id={labelId}>
              {connectAgentAddressText[language]}
            </span>
            <span className='bomdisoft-connect-agent-dialog-url-row'>
              <output aria-labelledby={labelId} className='bomdisoft-connect-agent-dialog-url' data-testid={`${testId}-address`}>
                {product?.serverAddress ?? ''}
              </output>
              <AddressCopy language={language} testId={`${testId}-copy`} value={product?.serverAddress ?? ''} />
            </span>
          </div>
          <ToggleGroup aria-label={connectAgentClientText[language]} className='bomdisoft-connect-agent-dialog-choice' data-testid={`${testId}-client`} onChange={next => next && setClient(next as AgentClient)} size='small' value={client}>
            {AGENT_CLIENTS.map(entry => (
              <ToggleButton data-testid={`${testId}-client-${entry}`} key={entry} value={entry}>
                {CLIENT_GUIDES[entry].name}
              </ToggleButton>
            ))}
          </ToggleGroup>
          <ol className='bomdisoft-connect-agent-dialog-steps' data-testid={`${testId}-steps`}>
            <li>
              {before}
              <a className='bomdisoft-connect-agent-dialog-step-link' data-testid={`${testId}-help`} href={guide.help} rel='noopener noreferrer' target='_blank'>
                {guide.link[language]}
              </a>
              {after}
            </li>
            <li>{guide.paste[language]}</li>
            <li data-testid={`${testId}-sign-in`}>{signIn[language]}</li>
          </ol>
        </div>
      </DialogContent>
      <DialogActions>
        <Button data-testid={`${testId}-done`} onClick={onClose} variant='text'>
          {connectAgentDoneText[language]}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
